'use client';
import JDIProductCardV2 from '@/components/productCard';
import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import style from '../style.module.scss';
// -----------------引入埋点-------------------
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/24934';
// ---------------- 监控 ---------------- //
import {
  HOME_EVENT_NAMES,
  homeBusinessLogger,
  HOME_TRACK_TYPE,
} from '@/utils/logger/instance/homeBusinessLogger';

import CountdownTimer from './CountdownTimer';

interface ProductSwiperProps {
  products: any[];
  currentTime: number;
  happyHourPeriod?: any;
}

const ProductSwiper = forwardRef<any, ProductSwiperProps>(
  ({ products, currentTime, happyHourPeriod }, ref) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useImperativeHandle(ref, () => ({
      getCurrentIndex: () => currentIndex,
      goToSlide: (index: number) => {
        if (index >= 0 && index < products.length) {
          changeSlide(index);
        }
      },
    }));

    const changeSlide = (newIndex: number) => {
      if (newIndex === currentIndex) return;

      setCurrentIndex(newIndex);
    };

    // 自动播放逻辑 - 简化版，瞬间切换
    useEffect(() => {
      if (products.length <= 1) return;

      const nextSlide = () => {
        setCurrentIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % products.length;
          return newIndex;
        });
      };

      intervalRef.current = setInterval(nextSlide, 2000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }, [products.length]);

    const genEventTrackingInfo = (product: any, index: number) => {
      const eventTrackingInfo = {} as { [key: string]: any };
      const json_param = { skuid: product.skuId, index: index };
      const expConfig = {
        useClick: false,
        stay_duration: 0,
        repeated: false,
        area_rate: 0.01,
      };
      eventTrackingInfo['productClickPoint'] = () => sendClickTrack.Home_Pro_FlashSales(json_param);
      eventTrackingInfo['productParam'] = json_param;
      eventTrackingInfo['productExpConfig'] = {
        event_id: EXPOSURE_RECORD.Home_Pro_FlashSales_Expo,
        config: expConfig,
        json_param: json_param,
      };
      return eventTrackingInfo;
    };

    if (!products || products.length === 0) {
      homeBusinessLogger({
        eventName: HOME_EVENT_NAMES.FLASH_SALES_NO_DATA,
        category: {
          type: HOME_TRACK_TYPE.CMS_JS_ERROR,
        },
        message: {
          msg: 'Flash sales 无数据',
        },
      });
      return null;
    }

    return (
      <div className={style.productSwiper}>
        <div className={style.fadeContainer}>
          {products.map((product, index) => {
            const productConfig = {
              ...product,
              trackingPointParams: genEventTrackingInfo(product, index),
              priority: index < 4,
            };

            // 只显示当前索引的商品，其他隐藏 - 瞬间切换，无过渡效果
            const isVisible = index === currentIndex;

            return (
              <div
                key={`${product.skuId}-${index}`}
                className={`${style.productItem} ${isVisible ? '' : style.hidden}`}
              >
                <div className={style.happyHourLabel}>
                  <span className={style.labelText}>Happy hour</span>
                  {happyHourPeriod === 2 ? (
                    <CountdownTimer
                      beginTime={product.beginTime}
                      endTime={product.endTime}
                      currentTime={product.currentTime}
                      className={style.timeDisplay}
                    />
                  ) : null}
                </div>
                <JDIProductCardV2 productData={productConfig} />
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

ProductSwiper.displayName = 'ProductSwiper';

export default ProductSwiper;
